import { BookingStep } from "@/constants/booking";
import { dexieDB } from "@/services/dexie-database";
import { LogLevel, LogService } from "@/services/logging";
import { MAX_SYNC_ATTEMPTS } from "@/services/sync-service";
import BookingModule from "@/store/modules/booking";
import FiskalyModule from "@/store/modules/fiskaly";
import PSSModule from "@/store/modules/pss";
import {
  AccountingState,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import {
  BaseOrderSession,
  BookingState,
  IProductSlotBooking,
  IUntypedBooking,
  SyncStatus,
} from "@/types/db/product-slot-booking";
import { OrderSessionState } from "@smeetz/order-common";
import dayjs from "dayjs";
import { syncUpdatedTimeslots } from "./timeslots";
import AppModule from "@/store/modules/app";
import PaymentService from "@/services/payment-service";
import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";
import { syncUpdatedRetails } from "./product-category";

/**
 * Removes all unfinished bookings that:
 * - Are **not** in a "Completed" (state: 3) or "To Refund" (state: 6) state.
 * - Do **not** have an Adyen card payment in progress.
 * - Have **not** been fully paid (i.e., partially paid with an uncompleted order session).
 */
export function clearUnfinishedBookings() {
  const bookings = Object.values(BookingModule.bookings);

  const unfinishedBookings = bookings.filter((booking) => {
    const { uuid, state, orderSessions, accountings } = booking;
    // Exclude completed and refundable bookings
    if (state === BookingState.Completed || state === BookingState.To_Refund) {
      return false;
    }

    // Check if there's an Adyen card payment in progress
    const hasAdyenAccountings = accountings.some(
      (acc) =>
        !acc.id &&
        acc.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT
    );

    if (hasAdyenAccountings) {
      // Exclude booking with Adyen payments in progress
      return false;
    }

    // Find the latest uncompleted order session (state: PENDING)
    const lastUncompleteOrderSession = orderSessions
      .filter((os) => os.state === OrderSessionState.PENDING)
      .sort(
        (a, b) =>
          dayjs(b.sessionStart).valueOf() - dayjs(a.sessionStart).valueOf()
      )[0];

    // Check if the booking is partially paid with an uncompleted order session
    const isPartiallyPaid = accountings.some(
      (acc) =>
        !acc.id &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT &&
        acc.state === AccountingState.Completed &&
        acc.orderSessionId === lastUncompleteOrderSession?.id
    );

    // Check if the booking is not marked as complete
    const hasNotBeenCompleted =
      BookingModule.bookingsSteps[uuid as string] !== BookingStep.CompleteOrder;

    if (hasNotBeenCompleted && isPartiallyPaid) {
      // Exclude bookings that are partially paid but not completed
      return false;
    }

    // Keep the rest of the bookings (unfinished and unpaid)
    return true;
  });

  if (unfinishedBookings.length === 0) return; // Exit early if no unfinished bookings

  // Remove all unfinished bookings
  unfinishedBookings.forEach((booking) => {
    BookingModule.removeBooking(booking);
    FiskalyModule.removeSignatureQrCodes(String(booking.uuid));
    FiskalyModule.removeTransaction(String(booking.uuid));
  });
}

/**
 * Skip post only if booking exists in db with status pending or
 */
export async function shouldSkipBookingPost(
  uuid: string | null | undefined
): Promise<{ skipPost: boolean; syncStatus?: SyncStatus }> {
  if (!uuid) {
    // skip if it doesn't have a uuid
    return { skipPost: true };
  }

  const dbBooking = await getUntypedBookingByUuid(uuid);

  if (!dbBooking) {
    return { skipPost: false };
  }

  const { syncStatus, attempts } = dbBooking;

  const shouldSkip =
    syncStatus === SyncStatus.Synced ||
    (syncStatus === SyncStatus.Failed && attempts >= MAX_SYNC_ATTEMPTS);

  return { skipPost: shouldSkip, syncStatus };
}

/**
 * Checks if a booking is posted or at least in queue for posting by checking for it in DexieDB
 * @param booking
 */
export async function isBookingInDexie(
  uuid: string | null | undefined
): Promise<boolean> {
  if (!uuid) {
    return false;
  }

  const dbBooking = await getUntypedBookingByUuid(uuid);

  if (dbBooking) {
    LogService.info(
      `Booking with UUID ${uuid} exists in Dexie, assuming already posted.`,
      {
        uuid: dbBooking.uuid,
        syncStatus: dbBooking.syncStatus,
        attempts: dbBooking.attempts,
      }
    );
    return true;
  } else {
    LogService.info(
      `Booking with UUID ${uuid} not found in Dexie, assuming not posted.`
    );
    return false;
  }
}

/**
 * Returns booking stored in Dexie by uuid
 * @param uuid
 */
export async function getUntypedBookingByUuid(uuid: string | null | undefined) {
  if (!uuid) {
    return;
  }
  const dbBooking = await dexieDB.untypedBookings
    .where("uuid")
    .equals(uuid)
    .first();
  return dbBooking;
}

export async function syncBookingAvailabilities(booking: IProductSlotBooking) {
  // 1- sync timeslots
  try {
    AppModule.setIsBackgroundLoading(true);
    await syncUpdatedTimeslots(booking.pssList);
  } catch (error) {
    LogService.info(
      `Availability: Error when synching timeslots for booking ${booking.uuid}`,
      { error },
      LogLevel.Error
    );
  } finally {
    AppModule.setIsBackgroundLoading(false);
  }
  // 2- sync retail
  try {
    AppModule.setIsBackgroundLoading(true);
    await syncUpdatedRetails(booking.pssList);
  } catch (error) {
    LogService.info(
      `Availability: Error when synching retail prices for booking ${booking.uuid}`,
      { error },
      LogLevel.Error
    );
  } finally {
    AppModule.setIsBackgroundLoading(false);
  }

  // check if the new availabilities are all enough for the current booking
  await BookingModule.updateBookingAvailabilityInfo();
  const isBookingCoveredByAvailabilities =
    BookingModule.isBookingCoveredByAvailabilities;
  // ignore availabilities if an Adyen payment is in progress to avoid removing pss while processing the payment
  if (!PaymentService.serviceId && !isBookingCoveredByAvailabilities) {
    ModalModule.setStatus(ModalStatus.AvailabilityInfo);
    return;
  }
}

/**
 * Accepts a booking object
 * Returns the newest (newest completedDate) order session object that has accounting entries linked to it
 */
export function getNewestNoneEmptyOrderSession(
  order: IProductSlotBooking
): BaseOrderSession | null {
  console.log("*** hi from getNewestNoneEmptyOrderSession");
  const orderSessions = order.orderSessions;
  if (orderSessions.length === 0) {
    console.log("no order sessions in order");
    return null;
  }

  if (order.accountings.length === 0) {
    console.log("no accountings in order");
    return null;
  }

  const accountingEntries = order.accountings
    .map((acc) => acc.accountingEntriesList)
    .flat();
  if (accountingEntries.length === 0) {
    console.log("no accountingEntries in order");
    return null;
  }

  // Lets now sort orderSessions based on their completionDate, newest completed first
  const sortedOrderSessions = orderSessions.sort((a, b) => {
    const aCompletedAt = a.completionDate
      ? dayjs(a.completionDate).valueOf()
      : 0;
    const bCompletedAt = b.completionDate
      ? dayjs(b.completionDate).valueOf()
      : 0;
    return bCompletedAt - aCompletedAt;
  });
  console.log("sortedOrderSessions");
  console.log(sortedOrderSessions);

  for (let i = 0; i < sortedOrderSessions.length; i++) {
    const os = sortedOrderSessions[i];
    const hasAccountingEntries = accountingEntries.some(
      (acc) => acc.orderSessionId === os.id
    );
    if (hasAccountingEntries) {
      return os;
    }
  }

  console.log("returning last null");
  return null;
}
